// src/BorkenburgLion.jsx
import React, { useId } from "react";
import { LION_PATH, VIEWBOX } from "./lionPath";

/*
  The mark is one traced silhouette, so the jaw is rigged with clip paths rather
  than separate artwork: the same path is drawn twice, once clipped to the snout
  and once clipped to everything else, and only the snout copy rotates.

  Geometry below is in viewBox units, read off the trace. Scanning the mark
  vertically through the muzzle gives bands of upper jaw (y≈230-375), mouth gap
  (y≈375-410), tongue (y≈410-520), then chest — so a box around the upper band
  isolates the jaw without touching the tongue or the body.

  SEAM_X   where the snout meets the skull; the head copy covers everything left of it
  TUCK_X   the jaw copy starts further left so spare black stays hidden under the head
  HINGE    the mouth corner, where the gape pivots (mirrored in Splash.css)

  The jaw only ever rotates anticlockwise (negative), which sweeps it under the
  head copy and keeps the join covered. Rotating it positive would swing the
  snout away from the skull and split the seam open, which is why the inhale in
  Splash.css opens the mouth slightly rather than clamping it shut.

  The tuck is interrupted across the eye's band. The eye is a counter — a hole in
  the head copy — and the jaw sits behind the head, so any tucked black level
  with the eye rotates leftward into view through it and fills the eye in. That
  showed from about -6deg onward. Leaving the strip out there costs nothing: the
  snout proper starts at SEAM_X and never reaches the eye.
*/
const SEAM_X = 560;
const TUCK_X = 525;
const JAW_TOP = 190;
const JAW_BOTTOM = 385;
const EYE_TOP = 228;
const EYE_BOTTOM = 348;
/* The two copies must not share an exact edge: abutting clips each antialias
   against it and the coverage sums to less than one, drawing a pale hairline
   down the seam at rest. A few units of overlap removes it, and stays well
   clear of the eye even at full gape. */
const SEAM_OVERLAP = 4;

// Roar rings radiate from just in front of the open muzzle. Kept in absolute
// viewBox coordinates so CSS can anchor the scale with transform-origin.
const MOUTH_X = 860;
const MOUTH_Y = 340;
const RING_R = 60;
const RING_HALF_ANGLE = 52;

function ringArc(cx, cy, r, halfAngle) {
  const a = (halfAngle * Math.PI) / 180;
  const dx = r * Math.cos(a);
  const dy = r * Math.sin(a);
  return (
    `M${(cx + dx).toFixed(2)} ${(cy - dy).toFixed(2)}` +
    `A${r} ${r} 0 0 1 ${(cx + dx).toFixed(2)} ${(cy + dy).toFixed(2)}`
  );
}

/**
 * The Borkenburg mark as vector, with the jaw rigged so a parent can animate it.
 * The animation itself lives in Splash.css, keyed off the splash's own class, so
 * the mark renders inert wherever else it is used.
 *
 * @param {{rings?: boolean, title?: string}} props  rings adds the roar arcs;
 *   title makes it a labelled image rather than decoration.
 */
export default function BorkenburgLion({ rings = false, title }) {
  const uid = useId();
  const headClip = `bb-head-${uid}`;
  const jawClip = `bb-jaw-${uid}`;

  return (
    <svg
      className="bb-lion"
      viewBox={VIEWBOX}
      role={title ? "img" : "presentation"}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
    >
      <defs>
        {/* Everything except the snout box: three rects, unioned by clipPath. */}
        <clipPath id={headClip}>
          <rect x="0" y="0" width={SEAM_X} height="1000" />
          <rect x="0" y="0" width="985" height={JAW_TOP} />
          <rect x="0" y={JAW_BOTTOM} width="985" height={1000 - JAW_BOTTOM} />
        </clipPath>
        {/* The snout, plus a tuck strip to its left, broken around the eye. */}
        <clipPath id={jawClip}>
          <rect
            x={SEAM_X - SEAM_OVERLAP}
            y={JAW_TOP}
            width={985 - SEAM_X + SEAM_OVERLAP}
            height={JAW_BOTTOM - JAW_TOP}
          />
          <rect
            x={TUCK_X}
            y={JAW_TOP}
            width={SEAM_X - TUCK_X}
            height={EYE_TOP - JAW_TOP}
          />
          <rect
            x={TUCK_X}
            y={EYE_BOTTOM}
            width={SEAM_X - TUCK_X}
            height={JAW_BOTTOM - EYE_BOTTOM}
          />
        </clipPath>
      </defs>

      {/* Jaw first, so it renders behind the head copy that hides its overlap. */}
      <g className="bb-lion-jaw" clipPath={`url(#${jawClip})`}>
        <path d={LION_PATH} />
      </g>
      <g clipPath={`url(#${headClip})`}>
        <path d={LION_PATH} />
      </g>

      {rings && (
        <g className="bb-lion-rings" aria-hidden="true">
          {[1, 2, 3].map((n) => (
            <path
              key={n}
              className={`bb-ring bb-ring-${n}`}
              d={ringArc(MOUTH_X, MOUTH_Y, RING_R, RING_HALF_ANGLE)}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </g>
      )}
    </svg>
  );
}
